import React from 'react'

import { alpha, Box, Stack, Typography, useTheme } from '@mui/material'
import { SquareStackIcon, UngroupIcon } from 'lucide-react'

import type { CertificateGroupDivisibility } from 'types'

// VRG-337 (§36 — extract on the second hand-roll): this was originally a
// local `DIVISIBILITY_OPTIONS` const + inline card markup inside
// CreateCertificatesModal (the create-wizard's group step). Grouping EXISTING
// certificates needs the exact same divisible/indivisible choice, so per §36
// this is now the shared component — CreateCertificatesModal was migrated to
// consume it too, rather than leaving a second hand-rolled copy.
//
// VRG-231 review item 5 (round 1) — the two cards used to carry DIFFERENT
// icon shapes (a split glyph vs a link glyph), which read as "two unrelated
// concepts" rather than "the same axis, two values". The cached Figma still
// (context/figma/x1ysld12jTYHeUeKslRcJl/1159-180513-create-cert-group-indivisible.png,
// 1337-126416-certs-list-opened-group.png) shows ONE glyph shape — a 4-diamond
// cluster (lucide's `ComponentIcon`) — tinted green for Divisible and orange
// for Indivisible, so round 1 unified both cards onto that one shape.
//
// VRG-231 review round 2 item 5 — SUPERSEDES round 1's shape choice, but keeps
// its COLORS. Round 2 flagged colour-only as a real accessibility gap (fails
// for colourblind users), and re-checking the Figma cache confirms round 1's
// own reading was correct: every divisibility frame in the cache (the two
// referenced above) uses the identical 4-diamond glyph for both values — there
// is no second SHAPE in the design to "match", only a second colour. So the
// shape distinction is sourced from lucide, not from the design, and that is
// a deliberate deviation from the literal cached frames rather than a silent
// redesign.
//
// VRG-231 review round 3 — SUPERSEDES round 2's specific pair. Round 2 borrowed
// each value's shape from an ACTION that related to it (`UngroupIcon` from
// "Split group", `LayersIcon` from "Create group"), which produced two
// collisions rather than one:
//
//   - `LayersIcon` meant BOTH "indivisible group" (a state) and "Create group"
//     (an action) — the collision that was reported.
//   - `UngroupIcon` meant BOTH "divisible group" (a state) and "Split group"
//     (an action), and those two CO-OCCUR ON THE SAME ROW: a divisible group's
//     row shows an Ungroup badge beside an Ungroup kebab item. Worse than the
//     reported one, and only found by auditing every call site.
//
// The organising rule that fixes both structurally, rather than by picking two
// arbitrarily different glyphs: **states are static shapes, actions are
// directional arrows.** So the badges become a shape FAMILY —
// `UngroupIcon` (divisible: rounded squares with a visible gap) and
// `SquareStackIcon` (indivisible: the same squares overlapping into one stack)
// — and the two group operations become visual inverses in arrow language,
// `MergeIcon` for Create group and `SplitIcon` for Split group. `LayersIcon`
// leaves the certificates domain entirely.
//
// Known and accepted: `UngroupIcon` is idiomatically a VERB ("ungroup") in
// every design tool, which is slightly off for a state badge. Kept anyway,
// because a user reads two separated squares rather than a name, it is already
// this value's badge (zero churn), and its pairing with SquareStack is the
// whole point. The alternative pair — `Layers2Icon` + `ComponentIcon` — shares
// no primitive between the two, so it reads as two icons rather than one axis.
export const DIVISIBILITY_OPTIONS: {
  value: CertificateGroupDivisibility
  label: string
  description: string
  icon: React.ReactNode
  color: (theme: any) => string
}[] = [
  {
    value: 'divisible',
    label: 'Divisible',
    description: 'Products that can be sold separately',
    icon: <UngroupIcon strokeWidth={1} />,
    color: theme => theme.palette.green[500],
  },
  {
    value: 'indivisible',
    label: 'Indivisible',
    description: 'Products that are only sold as a group',
    // VRG-231 review round 3 — was `LayersIcon`, which collided with the
    // "Create group" ACTION's glyph. The pair is now `UngroupIcon` (divisible)
    // and `SquareStackIcon` (indivisible): the SAME rounded-square primitive in
    // the same diagonal arrangement, at two separations. Ungroup's rects carry a
    // 4-unit gap; SquareStack's three overlap and touch. The contrast IS the
    // meaning — "related but separate" vs "one unit" — and separation is coarse
    // enough to survive the 20px badge, where shape is the only channel
    // available (the badge tints by certificate STATUS, not divisibility).
    icon: <SquareStackIcon strokeWidth={1} />,
    color: theme => theme.palette.orange[500],
  },
]

export interface DivisibilityOptionPickerProps {
  value: CertificateGroupDivisibility | null
  onChange: (value: CertificateGroupDivisibility) => void
  disabled?: boolean
}

/**
 * The divisible/indivisible selectable-card pair (§34 — tint on hover, brand
 * border only when selected; never a brand border on hover, that would
 * collide with the selected state).
 *
 * VRG-231 fix F — keyboard accessibility. This is a single-choice picker, so
 * it's structured as a radio group: `role="radiogroup"` (labelled
 * "Divisibility") wrapping two `role="radio"` options, each with
 * `aria-checked` mirroring `isSelected`. Tab order uses a ROVING tabIndex —
 * only the selected option (or the first, when nothing is selected yet) sits
 * in the tab sequence, matching how native `<input type="radio">` groups
 * behave (arrow keys move the selection, hence advance the roving index too
 * — see the ArrowLeft/ArrowRight handling below). Enter and Space both
 * activate, mirroring `MethodOption` (AddModal/shared.tsx) and every other
 * activatable card in this codebase.
 *
 * The `:focus-visible` ring is a THIRD, independent signal from hover
 * (tint) and selected (border) — it must not be confused with either, so it
 * is drawn as a ring only (the same soft brand ring every field uses per
 * §31 — `brand['600-6%']`, inlined here rather than via the shared
 * `fieldFocusRing` helper: that helper's `Theme` parameter type resolves
 * against `@repo/ui`'s own `@mui/material` instance, which this monorepo's
 * duplicate-MUI-install pnpm layout treats as structurally incompatible
 * with `apps/platform`'s — a pre-existing mismatch already hit repeatedly in
 * `DatePickerField.tsx`) and never changes the border colour itself.
 */
export const DivisibilityOptionPicker = ({ value, onChange, disabled }: DivisibilityOptionPickerProps) => {
  const theme = useTheme()

  const selectedIndex = DIVISIBILITY_OPTIONS.findIndex(option => option.value === value)
  const rovingIndex = selectedIndex >= 0 ? selectedIndex : 0

  const activate = (optionValue: CertificateGroupDivisibility) => {
    if (disabled) return
    onChange(optionValue)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>, index: number) => {
    if (disabled) return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      activate(DIVISIBILITY_OPTIONS[index].value)
      return
    }
    // Arrow-key movement between the two options — straightforward here since
    // there are only ever two, so left/right (and up/down, for a row layout
    // read either way) both just toggle between them.
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault()
      const next = (index + 1) % DIVISIBILITY_OPTIONS.length
      activate(DIVISIBILITY_OPTIONS[next].value)
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault()
      const prev = (index - 1 + DIVISIBILITY_OPTIONS.length) % DIVISIBILITY_OPTIONS.length
      activate(DIVISIBILITY_OPTIONS[prev].value)
    }
  }

  return (
    <Stack direction="row" spacing={3} role="radiogroup" aria-label="Divisibility">
      {DIVISIBILITY_OPTIONS.map((option, index) => {
        const isSelected = value === option.value
        return (
          <Box
            key={option.value}
            role="radio"
            aria-checked={isSelected}
            aria-disabled={disabled || undefined}
            tabIndex={disabled ? -1 : index === rovingIndex ? 0 : -1}
            onClick={() => activate(option.value)}
            onKeyDown={event => handleKeyDown(event, index)}
            sx={{
              flex: 1,
              p: 4,
              borderRadius: 3,
              cursor: disabled ? 'default' : 'pointer',
              border: `1px solid ${isSelected ? theme.palette.brand[500] : theme.palette.primary[100]}`,
              background: isSelected
                ? alpha(theme.palette.brand[500], 0.1)
                : theme.palette.background.default,
              outline: 'none',
              '&:hover': disabled
                ? undefined
                : {
                    background: isSelected
                      ? alpha(theme.palette.brand[500], 0.1)
                      : alpha(theme.palette.brand[500], 0.06),
                  },
              '&:focus-visible': disabled
                ? undefined
                : {
                    boxShadow: `0 0 0 3px ${theme.palette.brand['600-6%'] ?? alpha(theme.palette.brand[600], 0.16)}`,
                  },
            }}
          >
            <Stack spacing={2} alignItems="center" textAlign="center">
              <Box
                sx={theme => ({
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  color: option.color(theme),
                  background: alpha(option.color(theme), 0.12),
                })}
              >
                {option.icon}
              </Box>
              <Typography variant="body2">{option.label}</Typography>
              <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                {option.description}
              </Typography>
            </Stack>
          </Box>
        )
      })}
    </Stack>
  )
}

export default DivisibilityOptionPicker
